import { cn } from "@utils/tw"
import {
  ArticleCardFragment,
  DocumentCardFragment,
  PublicationCardFragment,
  ResponsiveImageFragment,
} from "../../types.graphql"
import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { Heading } from "@components/atoms/Heading"
import { Chip, ChipT } from "@components/atoms/Chip"
import { Link } from "eddev/routing"
import { VSpace } from "@components/layout/Spacer"
import { format, differenceInDays } from "date-fns"
import { Typography } from "@components/atoms/Typography"
import { CARET_RIGHT } from "@components/atoms/Icons"
import { Button, ButtonT } from "@components/atoms/Button"
import { useMemo } from "react"
import { useIsMobile } from "@hooks/useViewportDerived"
import { Logo } from "@components/atoms/Logo"
import { twMerge } from "tailwind-merge"

export const cardVariants = {
  link: "link",
  news: "news",
  document: "document",
  wide: "wide",
  campaign: "campaign",
  media: "media",
  navigation: "navigation",
  navigationDocument: "navigationDocument",
  documentAlt: "documentAlt",
  documentLight: "documentLight",
  header: "header",
  feature: "feature",
  breakout: "breakout",
}

type CardT = (ArticleCardFragment | DocumentCardFragment) & {
  label?: string
  video?: string
  type: keyof typeof cardVariants
  latest?: boolean
  additionalLink?: {
    url: string | null
    target?: string | null
    title: string | null
  }
}

function isArticleCard(card: CardT): card is ArticleCardFragment & {
  label?: string
  video?: string
  type: keyof typeof cardVariants
} {
  return (card as ArticleCardFragment).categories !== undefined && (card as ArticleCardFragment).tags !== undefined
}

function isDocumentCard(card: CardT): card is DocumentCardFragment & {
  label?: string
  video?: string
  type: keyof typeof cardVariants
} {
  return (
    (card as DocumentCardFragment).documentTopics !== undefined &&
    (card as DocumentCardFragment).documentTypes !== undefined
  )
}

// Ported from a `contentBlocks`-filter fallback (`blocks.filter(b => b.blockName
// === "core/paragraph")[0].innerHTML`) that requested the full parsed block
// tree just to read one paragraph — WPGraphQL's `contentBlocks` field runs
// eddev's custom parse_blocks()-based resolver on every card in a list,
// which is exactly what triggered "contentBlocks were selected N times"
// perf warnings on list/archive queries. `content` (the standard rendered
// post_content HTML) is a plain WPGraphQL field with no such per-post
// parsing cost, so pull the first paragraph out of that instead.
// No character classes (`[...]`) in this regex on purpose — Tailwind's JIT
// content scanner does a naive bracket-matching pass over every scanned
// file looking for arbitrary-value candidates, and a `[...]` inside a plain
// JS regex literal is enough to throw it off for the rest of the scan batch,
// silently dropping real Tailwind candidates from *other* files (confirmed:
// this exact regex was corrupting Navigation.tsx's classes into never
// generating any CSS). `.*?` + the `s` (dotAll) flag matches identically to
// `[^>]*`/`[\s\S]*?` for this use case, with zero literal brackets.
function getFirstParagraphHtml(html?: string | null) {
  const match = html?.match(/<p.*?>(.*?)<\/p>/s)
  return match ? match[1] : ""
}

export const Card = (props: CardT) => {
  const { uri, title, label, featuredImage, video, date, type } = props
  const isMobile = useIsMobile()

  const chips = useMemo(() => {
    if (isArticleCard(props)) {
      return props?.categories?.nodes?.concat(props?.tags?.nodes ?? [])
    }

    if (isDocumentCard(props)) {
      return props?.documentTopics?.nodes?.concat(props?.documentTypes?.nodes ?? [])
    }

    return []
  }, [props])

  const subtitle = useMemo(() => {
    if (isDocumentCard(props)) return props.documentMeta?.subtitle ?? ""
    return ""
  }, [props])

  if (type === "link") {
    return (
      <div className="w-full flex rounded-md gap-4 relative overflow-hidden bg-bg transition-[background-color] ease-movement duration-300 [&_img]:transition-transform [&_img]:ease-movement [&_img]:duration-300 [&_video]:transition-transform [&_video]:ease-movement [&_video]:duration-300 p-2 border border-grey [&_a]:no-underline aaa-animate hover:-translate-y-[3px] hover:shadow-[0px_6px_12px_0px_rgba(34,34,34,0.08),0px_1px_3px_0px_rgba(34,34,34,0.1)]">
        {uri && <Link className="absolute inset-0" href={uri} />}
        {featuredImage?.node && (
          <div className="aaa-animate relative pointer-events-none [&_div]:block [&_div]:w-full [&_div]:absolute [&_div]:inset-0 [&>div]:!h-full [&>div_img]:block rounded-[6px] overflow-hidden w-[calc(100%*(1/3))] min-w-[calc(100%*(1/3))] h-0 pt-[calc(100%*(1/3))]">
            <ResponsiveImage {...featuredImage} aspect={1} />
          </div>
        )}
        <div className="flex flex-col pointer-events-none [&_h1]:type-title-xl [&_h3]:type-title-m [&_h3]:my-0 [&_strong]:!font-[inherit] self-stretch justify-between">
          <Heading as="h5">{title}</Heading>

          {chips && (
            <div className="flex gap-x-1 gap-y-1 flex-wrap mt-3 relative z-[1] pointer-events-auto">
              {chips.map((category, i) => (
                <Chip key={i} color="grey" href={category?.uri ?? "#"}>
                  {category.name}
                </Chip>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  if (type === "news") {
    return (
      <div className="w-full flex rounded-md gap-4 relative overflow-hidden bg-bg transition-[background-color] ease-movement duration-300 [&_img]:transition-transform [&_img]:ease-movement [&_img]:duration-300 [&_video]:transition-transform [&_video]:ease-movement [&_video]:duration-300 flex-col p-4 border border-grey self-stretch [&_a]:no-underline aaa-animate hover:-translate-y-[3px] hover:shadow-[0px_6px_12px_0px_rgba(34,34,34,0.08),0px_1px_3px_0px_rgba(34,34,34,0.1)]">
        {uri && <Link className="absolute inset-0" href={uri} />}

        <div className="aaa-animate relative pointer-events-none [&_div]:block [&_div]:w-full [&_div]:absolute [&_div]:inset-0 [&>div]:!h-full [&>div_img]:block rounded-[6px] overflow-hidden w-full h-0 pt-[calc(100%*(4/6))] bg-grey [&_svg]:absolute [&_svg]:top-1/2 [&_svg]:left-1/2 [&_svg]:-translate-x-1/2 [&_svg]:-translate-y-1/2 [&_svg]:text-white [&_svg]:w-[40%] [&_svg]:h-auto [&_svg_path]:fill-white">
          {featuredImage?.node ? <ResponsiveImage {...featuredImage.node} aspect={6 / 4} /> : <Logo />}
        </div>

        <div className="flex flex-col pointer-events-none [&_h1]:type-title-xl [&_h3]:type-title-m [&_h3]:my-0 [&_strong]:!font-[inherit] grow justify-between">
          <div className="flex flex-col gap-2">
            {date && (
              <Typography as="span" variant="caps" className="text-dark-grey">
                {format(date, "dd.MM.yyyy")}
              </Typography>
            )}
            <Heading as="h5">{title}</Heading>
          </div>

          {chips && (
            <div className="flex gap-x-1 gap-y-1 flex-wrap mt-3 relative z-[1] pointer-events-auto">
              {chips.map((category, i) => (
                <Chip key={i} color="grey" href={category?.uri ?? "#"}>
                  {category.name}
                </Chip>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  if (type === "document") {
    return (
      <div className="w-full flex rounded-md gap-4 relative overflow-hidden transition-[background-color] ease-movement duration-300 [&_img]:transition-transform [&_img]:ease-movement [&_img]:duration-300 [&_video]:transition-transform [&_video]:ease-movement [&_video]:duration-300 p-4 border border-light-grey bg-light-grey [&_a]:no-underline hover:border-grey aaa-animate hover:-translate-y-[3px] hover:shadow-[0px_6px_12px_0px_rgba(34,34,34,0.08),0px_1px_3px_0px_rgba(34,34,34,0.1)]">
        {uri && <Link className="absolute inset-0" href={uri} />}
        <div className="aaa-animate relative pointer-events-none [&_div]:block [&_div]:w-full [&_div]:absolute [&_div]:inset-0 [&>div]:!h-full [&>div_img]:block rounded-[6px] overflow-hidden w-[calc(100%*(1/4))] min-w-[calc(100%*(1/4))] h-0 pt-[35.3%] bg-white [&_svg]:absolute [&_svg]:top-1/2 [&_svg]:left-1/2 [&_svg]:-translate-x-1/2 [&_svg]:-translate-y-1/2 [&_svg]:text-mid-grey [&_svg]:w-1/2 [&_svg]:h-auto [&_svg_path]:fill-mid-grey">
          {featuredImage?.node ? <ResponsiveImage {...featuredImage.node} aspect={0.71} /> : <Logo />}
        </div>

        <div className="flex flex-col pointer-events-none [&_h1]:type-title-xl [&_h3]:type-title-m [&_h3]:my-0 [&_strong]:!font-[inherit] justify-between">
          <div>
            <span className="type-text-m">{subtitle}</span>
            <VSpace initial={1} />
            <h4 className="!type-title-xs">{title}</h4>
          </div>

          {chips && (
            <div className="flex gap-x-1 gap-y-1 flex-wrap mt-3 relative z-[1] pointer-events-auto justify-self-end">
              {chips.map((category, i) => (
                <Chip key={i} color="outline" href={category?.uri ?? "#"}>
                  {category.name}
                </Chip>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  if (type === "documentLight") {
    return (
      <div className="w-full flex rounded-md gap-4 relative overflow-hidden transition-[background-color] ease-movement duration-300 [&_img]:transition-transform [&_img]:ease-movement [&_img]:duration-300 [&_video]:transition-transform [&_video]:ease-movement [&_video]:duration-300 p-4 border border-light-grey bg-white [&_a]:no-underline hover:border-grey [&>div:first-of-type]:border [&>div:first-of-type]:border-light-grey aaa-animate hover:-translate-y-[3px] hover:shadow-[0px_6px_12px_0px_rgba(34,34,34,0.08),0px_1px_3px_0px_rgba(34,34,34,0.1)]">
        {uri && <Link className="absolute inset-0" href={uri} />}
        <div className="aaa-animate relative pointer-events-none [&_div]:block [&_div]:w-full [&_div]:absolute [&_div]:inset-0 [&>div]:!h-full [&>div_img]:block rounded-[6px] overflow-hidden w-[calc(100%*(1/4))] min-w-[calc(100%*(1/4))] h-0 pt-[35.3%] bg-white [&_svg]:absolute [&_svg]:top-1/2 [&_svg]:left-1/2 [&_svg]:-translate-x-1/2 [&_svg]:-translate-y-1/2 [&_svg]:text-mid-grey [&_svg]:w-1/2 [&_svg]:h-auto [&_svg_path]:fill-mid-grey">
          {featuredImage?.node ? <ResponsiveImage {...featuredImage.node} aspect={0.71} /> : <Logo />}
        </div>

        <div className="flex flex-col pointer-events-none [&_h1]:type-title-xl [&_h3]:type-title-m [&_h3]:my-0 [&_strong]:!font-[inherit] justify-between">
          <div>
            <span className="type-text-m">{subtitle}</span>
            <VSpace initial={1} />
            <h4 className="!type-title-xs">{title}</h4>
          </div>

          {chips && (
            <div className="flex gap-x-1 gap-y-1 flex-wrap mt-3 relative z-[1] pointer-events-auto justify-self-end">
              {chips.map((category, i) => (
                <Chip key={i} color="outline" href={category?.uri ?? "#"}>
                  {category.name}
                </Chip>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  if (type === "wide") {
    return (
      <div className="w-full flex rounded-md gap-4 relative overflow-hidden transition-[background-color] ease-movement duration-300 [&_img]:transition-transform [&_img]:ease-movement [&_img]:duration-300 [&_video]:transition-transform [&_video]:ease-movement [&_video]:duration-300 h-[112px] text-white bg-light-grey [&_label]:type-label-caps lg:h-[125px] aaa-animate hover:-translate-y-[3px] hover:shadow-[0px_6px_12px_0px_rgba(34,34,34,0.08),0px_1px_3px_0px_rgba(34,34,34,0.1)]">
        <div className="flex flex-col pointer-events-none [&_h1]:type-title-xl [&_h3]:type-title-m [&_h3]:my-0 [&_strong]:!font-[inherit] z-[1] relative p-7 self-stretch justify-start lg:p-5">
          <Heading as="label">{label}</Heading>
          <VSpace initial={0} />
          <Heading as="h4">{title}</Heading>
        </div>
        {featuredImage?.node && (
          <div className="aaa-animate pointer-events-none [&_div]:block [&_div]:w-full [&_div]:absolute [&_div]:inset-0 [&>div]:!h-full [&>div_img]:block absolute inset-0 z-0">
            <ResponsiveImage {...featuredImage.node} />
          </div>
        )}
      </div>
    )
  }

  if (type === "campaign") {
    return (
      <div className="w-full flex rounded-md gap-4 relative overflow-hidden bg-bg transition-[background-color] ease-movement duration-300 [&_img]:transition-transform [&_img]:ease-movement [&_img]:duration-300 [&_video]:transition-transform [&_video]:ease-movement [&_video]:duration-300 h-[144px] text-white hover:-translate-y-[3px] hover:shadow-[0px_6px_12px_0px_rgba(34,34,34,0.08),0px_1px_3px_0px_rgba(34,34,34,0.1)]">
        {uri && <Link className="absolute inset-0" href={uri} />}
        <div className="flex flex-col pointer-events-none [&_h1]:type-title-xl [&_h3]:type-title-m [&_h3]:my-0 [&_strong]:!font-[inherit] h-full z-[1] relative p-4 justify-between">
          <Heading as="h5">{title}</Heading>

          {chips && (
            <div className="flex gap-x-1 gap-y-1 flex-wrap mt-3 relative z-[1] pointer-events-auto">
              {chips.map((category, i) => (
                <Chip key={i} color="white" href={category?.uri ?? "#"}>
                  {category.name}
                </Chip>
              ))}
            </div>
          )}
        </div>
        {featuredImage?.node && (
          <div className="aaa-animate pointer-events-none [&_div]:block [&_div]:w-full [&_div]:absolute [&_div]:inset-0 [&>div]:!h-full [&>div_img]:block absolute inset-0 z-0">
            <ResponsiveImage {...featuredImage.node} />
          </div>
        )}
      </div>
    )
  }

  if (type === "media") {
    if (!video || !featuredImage?.node) return <></>

    return (
      <div className="w-full flex rounded-md gap-4 relative overflow-hidden bg-bg transition-[background-color] ease-movement duration-300 [&_img]:transition-transform [&_img]:ease-movement [&_img]:duration-300 [&_video]:transition-transform [&_video]:ease-movement [&_video]:duration-300 aaa-animate h-[192px] [&_video]:absolute [&_video]:w-full [&_video]:h-full [&_video]:object-cover lg:h-[266px] hover:-translate-y-[3px] hover:shadow-[0px_6px_12px_0px_rgba(34,34,34,0.08),0px_1px_3px_0px_rgba(34,34,34,0.1)]">
        {video ? (
          <video autoPlay muted loop playsInline>
            <source src={video} type="video/mp4" />
          </video>
        ) : (
          <ResponsiveImage {...featuredImage.node} />
        )}
      </div>
    )
  }

  if (type === "navigation") {
    return (
      <Link
        className="w-full rounded-md flex gap-3 py-2 px-3 transition-colors ease-movement duration-300 justify-between items-center [&_i]:text-white [&_i]:transition-colors [&_i]:ease-movement [&_i]:duration-300 hover:bg-light-grey [&:hover_i]:text-black"
        href={uri}
      >
        {featuredImage?.node && (
          <div className="aaa-animate relative pointer-events-none [&_div]:block [&_div]:w-full [&_div]:absolute [&_div]:inset-0 [&>div]:!h-full [&>div_img]:block rounded-[6px] overflow-hidden w-[100px] min-w-[100px] h-0 pt-[70px]">
            {/* aspect={96 / 64} */}
            <ResponsiveImage {...featuredImage.node} max />
          </div>
        )}
        <div className="flex flex-col justify-center pointer-events-none [&_h1]:type-title-xl [&_h3]:my-0 [&_strong]:!font-[inherit] gap-1 items-start grow [&_h3]:type-title-xs">
          {props.latest && <Chip color="yellow">Latest</Chip>}
          <span className="type-title-xs">{title}</span>
          {date && (
            <Typography as="span" variant="caps">
              {differenceInDays(new Date(), date)} days ago
            </Typography>
          )}
        </div>
        <i>{CARET_RIGHT}</i>
      </Link>
    )
  }

  if (type === "navigationDocument") {
    return (
      <Link
        className="w-full rounded-md flex gap-3 py-2 px-3 transition-colors ease-movement duration-300 justify-between items-center [&_i]:text-white [&_i]:transition-colors [&_i]:ease-movement [&_i]:duration-300 hover:bg-light-grey [&:hover_i]:text-black"
        href={uri}
      >
        {featuredImage?.node && (
          <div className="aaa-animate relative pointer-events-none [&_div]:block [&_div]:w-full [&_div]:absolute [&_div]:inset-0 [&>div]:!h-full [&>div_img]:block rounded-[6px] overflow-hidden w-[70px] h-[70px] flex items-center justify-center bg-black p-1 [&>div]:relative [&>div]:w-full  [&>div]:p-0">
            <ResponsiveImage {...featuredImage.node} objectFit="contain" />
          </div>
        )}
        <div className="flex flex-col justify-center pointer-events-none [&_h1]:type-title-xl [&_h3]:my-0 [&_strong]:!font-[inherit] gap-1 items-start grow [&_h3]:type-title-xs">
          {date && (
            <Typography as="span" variant="caps">
              {format(date, "dd.MM.yyyy")}
            </Typography>
          )}
          <span className="type-title-xs">
            {title}
            {subtitle && <>, {subtitle}</>}
          </span>
          {chips && (
            <div className={cn("flex gap-x-1 gap-y-1 flex-wrap mt-3 relative z-[1] pointer-events-auto", "mt-1")}>
              {chips.map((category, i) => (
                <Chip key={i} color="outline">
                  {category.name}
                </Chip>
              ))}
            </div>
          )}
        </div>
        <i>{CARET_RIGHT}</i>
      </Link>
    )
  }

  if (type === "documentAlt") {
    return (
      <Link
        className="flex gap-4 content-box !h-auto my-1 rounded-md overflow-hidden p-4 transition-colors ease-movement duration-300 justify-between items-center bg-light-grey w-full text-black no-underline [&_div.content]:flex [&_div.content]:flex-col [&_i]:text-black hover:bg-grey hover:text-black"
        href={uri}
      >
        {featuredImage?.node && (
          <div className="aaa-animate relative pointer-events-none [&_div]:block [&_div]:w-full [&_div]:absolute [&_div]:inset-0 [&>div]:!h-full [&>div_img]:block rounded-[6px] overflow-hidden w-[94px] min-w-[94px] max-w-[94px] h-0 pt-[132px] bg-white [&_svg]:absolute [&_svg]:top-1/2 [&_svg]:left-1/2 [&_svg]:-translate-x-1/2 [&_svg]:-translate-y-1/2 [&_svg]:text-mid-grey [&_svg]:w-1/2 [&_svg]:h-auto [&_svg_path]:fill-mid-grey">
            <ResponsiveImage {...featuredImage.node} aspect={94 / 132} />
          </div>
        )}
        <div className="flex flex-col pointer-events-none [&_h1]:type-title-xl [&_h3]:my-0 [&_strong]:!font-[inherit] gap-1 items-start justify-between h-full grow [&_h3]:type-title-xs">
          <div className="content flex flex-col gap-2">
            {date && (
              <Typography as="span" variant="caps">
                {format(date, "dd.MM.yyyy")}
              </Typography>
            )}
            <h5>
              {title}
              {subtitle && <>, {subtitle}</>}
            </h5>
          </div>

          {chips && (
            <div className={cn("flex gap-x-1 gap-y-1 flex-wrap mt-3 relative z-[1] pointer-events-auto", "mt-1")}>
              {chips.map((category, i) => (
                <Chip key={i} color="outline">
                  {category.name}
                </Chip>
              ))}
            </div>
          )}
        </div>
        <i>{CARET_RIGHT}</i>
      </Link>
    )
  }

  if (type === "header") {
    const exerpt = () => {
      if (isArticleCard(props)) {
        if (!props.excerpt?.includes("[&hellip;]")) return props.excerpt
        return getFirstParagraphHtml(props.content)
      }

      return getFirstParagraphHtml(props.content)
    }

    return (
      <div className="w-full flex rounded-md relative overflow-hidden bg-bg transition-[background-color] ease-movement duration-300 [&_img]:transition-transform [&_img]:ease-movement [&_img]:duration-300 [&_video]:transition-transform [&_video]:ease-movement [&_video]:duration-300 aaa-animate flex-col h-auto gap-0 md:flex-row-reverse md:justify-between md:w-[calc(100%+var(--spacing-4)*2)] md:-ml-4 md:p-4 md:box-border md:wp:w-full md:wp:!ml-0 hover:-translate-y-[3px] hover:shadow-[0px_6px_12px_0px_rgba(34,34,34,0.08),0px_1px_3px_0px_rgba(34,34,34,0.1)]">
        {uri && <Link className="absolute inset-0" href={uri} />}
        {featuredImage?.node && (
          <div className="aaa-animate relative pointer-events-none [&_div]:block [&_div]:w-full [&_div]:absolute [&_div]:inset-0 [&>div]:!h-full [&>div_img]:block w-full h-0 overflow-hidden pt-[calc(100%*(4/6))] bg-light-grey [&_svg]:absolute [&_svg]:top-1/2 [&_svg]:left-1/2 [&_svg]:-translate-x-1/2 [&_svg]:-translate-y-1/2 [&_svg]:text-white [&_svg]:w-[40%] [&_svg]:h-auto [&_svg_path]:fill-white md:w-[calc((100%-var(--tw-grid-gutter))*(6/12))] md:pt-[calc(((100%-var(--tw-grid-gutter))*(6/12))*(4/6))] md:h-auto md:rounded-[6px]">
            <ResponsiveImage {...featuredImage.node} aspect={6 / 4} />
          </div>
        )}
        <div className="flex flex-col justify-center pointer-events-none [&_h1]:type-title-xl [&_h3]:type-title-m [&_h3]:my-0 [&_strong]:!font-[inherit] p-6 items-start gap-5 md:w-[calc((100%-var(--tw-grid-gutter))*(5.5/12))] md:justify-start md:p-0 xl:w-[calc((100%-var(--tw-grid-gutter))*(5/12))] xl:min-h-[450px]">
          <Heading as="h1">{title}</Heading>
          <Typography variant="text-m" as="div" dangerouslySetInnerHTML={{ __html: exerpt() ?? "" }} />

          <div className="flex flex-wrap gap-4 relative z-[5] pointer-events-auto mt-7 w-full flex-col md:flex-row">
            {props?.additionalLink && (
              <Button
                color="yellow"
                size="small"
                icon={props.additionalLink.target ? "ARROW_UP_RIGHT" : "ARROW_RIGHT"}
                iconStyle="right"
                href={props.additionalLink.url ?? undefined}
                target={props.additionalLink.target ? "_blank" : undefined}
              >
                {props.additionalLink.title}
              </Button>
            )}
            <Button color="grey" size="small" icon="INFO" iconStyle="right" href={uri ? uri : undefined}>
              Read more
            </Button>
          </div>

          {chips && (
            <div className="flex gap-x-1 gap-y-1 flex-wrap mt-3 z-[1] pointer-events-auto absolute top-0 left-0 px-3 md:relative md:px-0 md:order-[-1]">
              {chips.map((category, i) => (
                <Chip key={i} color="grey" href={category?.uri ?? undefined}>
                  {category.name}
                </Chip>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  if (type === "feature") {
    const exerpt = () => {
      if (isArticleCard(props)) {
        if (!props.excerpt?.includes("[&hellip;]")) return props.excerpt
        return getFirstParagraphHtml(props.content)
      }

      return getFirstParagraphHtml(props.content)
    }

    return (
      <div className="w-full flex rounded-md relative overflow-hidden bg-bg transition-[background-color] ease-movement duration-300 [&_img]:transition-transform [&_img]:ease-movement [&_img]:duration-300 [&_video]:transition-transform [&_video]:ease-movement [&_video]:duration-300 aaa-animate flex-col h-auto gap-0 md:flex-row-reverse md:justify-between md:w-[calc(100%+var(--spacing-4)*2)] md:-ml-4 md:p-4 md:box-border md:wp:w-full md:wp:!ml-0 hover:-translate-y-[3px] hover:shadow-[0px_6px_12px_0px_rgba(34,34,34,0.08),0px_1px_3px_0px_rgba(34,34,34,0.1)]">
        {uri && <Link className="absolute inset-0" href={uri} />}
        {featuredImage?.node && (
          <div className="aaa-animate relative pointer-events-none [&_div]:block [&_div]:w-full [&_div]:absolute [&_div]:inset-0 [&>div]:!h-full [&>div_img]:block w-full h-0 overflow-hidden pt-[calc(100%*(4/6))] bg-light-grey [&_svg]:absolute [&_svg]:top-1/2 [&_svg]:left-1/2 [&_svg]:-translate-x-1/2 [&_svg]:-translate-y-1/2 [&_svg]:text-white [&_svg]:w-[40%] [&_svg]:h-auto [&_svg_path]:fill-white md:w-[calc((100%-var(--tw-grid-gutter))*(6/12))] md:pt-[calc(((100%-var(--tw-grid-gutter))*(6/12))*(4/6))] md:h-auto md:rounded-[6px]">
            <ResponsiveImage {...featuredImage.node} aspect={6 / 4} />
          </div>
        )}
        <div className="flex flex-col justify-center pointer-events-none [&_h1]:type-title-xl [&_h3]:type-title-m [&_h3]:my-0 [&_strong]:!font-[inherit] p-6 items-start gap-5 md:w-[calc((100%-var(--tw-grid-gutter))*(5.5/12))] md:justify-start md:p-0 xl:w-[calc((100%-var(--tw-grid-gutter))*(5/12))] xl:min-h-[450px]">
          <Heading as="h2" variant="h1">
            {title}
          </Heading>
          <Typography variant="text-s" as="div" dangerouslySetInnerHTML={{ __html: exerpt() ?? "" }} />

          <div className="flex flex-wrap gap-4 relative z-[5] pointer-events-auto mt-7 w-full flex-col md:flex-row">
            {props?.additionalLink && (
              <Button
                color="yellow"
                size="small"
                icon={props.additionalLink.target ? "ARROW_UP_RIGHT" : "ARROW_RIGHT"}
                iconStyle="right"
                href={props.additionalLink.url ?? undefined}
                target={props.additionalLink.target ? "_blank" : undefined}
              >
                {props.additionalLink.title}
              </Button>
            )}
            <Button color="grey" size="small" icon="INFO" iconStyle="right" href={uri ? uri : undefined}>
              Read more
            </Button>
          </div>

          {chips && (
            <div className="flex gap-x-1 gap-y-1 flex-wrap mt-3 z-[1] pointer-events-auto absolute top-0 left-0 px-3 md:relative md:px-0 md:order-[-1]">
              {chips.map((category, i) => (
                <Chip key={i} color="grey" href={category?.uri ?? undefined}>
                  {category.name}
                </Chip>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  if (type === "breakout") {
    const exerpt = () => {
      if (isArticleCard(props)) {
        if (!props.excerpt?.includes("[&hellip;]")) return props.excerpt
        return getFirstParagraphHtml(props.content)
      }

      return getFirstParagraphHtml(props.content)
    }

    return (
      <div className="w-full flex rounded-md relative overflow-hidden transition-[background-color] ease-movement duration-300 [&_img]:transition-transform [&_img]:ease-movement [&_img]:duration-300 [&_video]:transition-transform [&_video]:ease-movement [&_video]:duration-300 bg-yellow flex-col h-auto gap-0 md:flex-row md:justify-between md:w-[calc(100%+var(--spacing-4)*2)] md:-ml-4 md:box-border md:wp:w-full md:wp:!ml-0 hover:-translate-y-[3px] hover:shadow-[0px_6px_12px_0px_rgba(34,34,34,0.08),0px_1px_3px_0px_rgba(34,34,34,0.1)]">
        {uri && <Link className="absolute inset-0" href={uri} />}
        {featuredImage?.node && (
          <div className="aaa-animate relative pointer-events-none [&_div]:block [&_div]:w-full [&_div]:absolute [&_div]:inset-0 [&>div]:!h-full [&>div_img]:block w-full h-0 overflow-hidden pt-[calc(100%*(4/6))] md:w-[calc(100%*(6/12))] md:pt-[calc(((100%-var(--tw-grid-gutter))*(6/12))*(4/6))] md:h-auto md:rounded-[6px_0_0_6px]">
            <ResponsiveImage {...featuredImage.node} aspect={6 / 4} />
          </div>
        )}
        <div className="flex flex-col pointer-events-none [&_h1]:type-title-xl [&_h3]:type-title-m [&_h3]:my-0 [&_strong]:!font-[inherit] p-6 items-start gap-6 md:w-[calc(100%*(6/12))] md:justify-start md:p-6 lg:min-h-[450px] lg:px-8">
          <Heading as="h1">{title}</Heading>
          <Typography variant="text-m" as="div" dangerouslySetInnerHTML={{ __html: exerpt() ?? "" }} />
          <Button color="grey" size="small" href={uri ? uri : undefined}>
            Read more
          </Button>

          {chips && (
            <div className="flex gap-x-1 gap-y-1 flex-wrap mt-3 z-[1] pointer-events-auto absolute top-0 left-0 px-3 md:relative md:px-0 md:order-[-1]">
              {chips.map((category, i) => (
                <Chip key={i} color={isMobile ? "grey" : "black"} href={category?.uri ?? "#"}>
                  {category.name}
                </Chip>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }
}

export const PublicationGroupCard = (props: PublicationCardFragment) => {
  const { name, uri, description, documents } = props
  const featuredImage = props.taxonomyPublications?.heroImage
  const isMobile = useIsMobile()
  const chips = useMemo(() => {
    const collection: { name: string | null; uri: string | null }[] = []
    props.documents?.nodes.forEach((doc) => {
      doc.documentTopics?.nodes.forEach((topic) => {
        if (!collection.some((col) => col.uri === topic.uri)) collection.push(topic)
      })

      doc.documentTypes?.nodes.forEach((type) => {
        if (!collection.some((col) => col.uri === type.uri)) collection.push(type)
      })
    })
    return collection
  }, [props])

  return (
    <div className="w-full flex rounded-md gap-4 relative overflow-hidden bg-bg transition-[background-color] ease-movement duration-300 [&_img]:transition-transform [&_img]:ease-movement [&_img]:duration-300 [&_video]:transition-transform [&_video]:ease-movement [&_video]:duration-300 flex-col border border-grey self-stretch [&_a]:no-underline md:flex-row md:justify-between md:py-5 md:px-6 aaa-animate hover:-translate-y-[3px] hover:shadow-[0px_6px_12px_0px_rgba(34,34,34,0.08),0px_1px_3px_0px_rgba(34,34,34,0.1)]">
      {uri && <Link className="absolute inset-0" href={uri} />}
      <div className="aaa-animate relative pointer-events-none [&_div]:block [&_div]:w-full [&_div]:absolute [&_div]:inset-0 [&>div]:!h-full [&>div_img]:block w-full rounded-[6px_6px_0_0] overflow-hidden h-0 pt-[calc(100%*(194/344))] bg-light-grey [&>div]:mt-[36px] [&_svg]:absolute [&_svg]:top-1/2 [&_svg]:left-1/2 [&_svg]:-translate-x-1/2 [&_svg]:-translate-y-1/2 [&_svg]:text-white [&_svg]:w-[40%] [&_svg]:h-auto [&_svg_path]:fill-white md:w-[calc((100%-var(--spacing-7))*(5/12))] md:pt-[calc(((100%-var(--spacing-7))*(5/12))*(276/408))] md:rounded-[6px] md:order-2 md:border md:border-grey">
        {featuredImage ? <ResponsiveImage {...featuredImage} aspect={isMobile ? 344 / 194 : 408 / 276} /> : <Logo />}
      </div>
      <div className="flex flex-col pointer-events-none [&_h1]:type-title-xl [&_h3]:type-title-m [&_h3]:my-0 [&_strong]:!font-[inherit] p-6 gap-5 justify-start md:justify-between md:w-[calc((100%-var(--spacing-7))*(6/12))] md:order-1 md:p-0">
        <div className="flex flex-col gap-5">
          {chips && (
            <div className="flex gap-x-1 gap-y-1 flex-wrap mt-3 z-[1] pointer-events-auto absolute inset-0 px-3 md:relative md:px-0 md:order-[-1] justify-self-end">
              {chips.map((category, i) => (
                <Chip key={i} color={isMobile ? "white" : "outline"} href={category?.uri ?? "#"}>
                  {category.name}
                </Chip>
              ))}
            </div>
          )}

          <Heading as="h3" variant="h2">
            {name}
          </Heading>
          <Typography as="p" dangerouslySetInnerHTML={{ __html: description ?? "" }} />
        </div>
        <div className="flex flex-col gap-5">
          <div
            className={cn(
              "flex flex-wrap gap-4 relative z-[5] pointer-events-auto mt-7 w-full flex-col md:flex-row",
              "mt-0",
            )}
          >
            {documents?.nodes && (
              <Button color="yellow" size="small" icon="DOWNLOAD" iconStyle="right" href={documents.nodes[0].uri!}>
                Read latest report
              </Button>
            )}

            {props.taxonomyPublications?.liveDataLink?.uri && (
              <Button
                color="grey"
                size="small"
                icon="GRAPH"
                iconStyle="right"
                href={props.taxonomyPublications.liveDataLink.uri}
              >
                View live data
              </Button>
            )}

            {uri && (
              <Button color="grey" size="small" icon="INFO" iconStyle="right" href={uri}>
                Find out more
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

type ManualCardT = {
  type: "single" | "multi"
  chip?: ChipT
  title?: string
  content?: string
  image?: ResponsiveImageFragment
  imageLink?: string
  imagePosition?: boolean
  primaryLink?: ButtonT
  secondaryLink?: ButtonT
}

export const ManualCards = (props: ManualCardT) => {
  const isMobile = useIsMobile()

  if (props.type === "single") {
    return (
      <div
        className={twMerge(
          "w-full flex rounded-md relative overflow-hidden bg-bg flex-col gap-0 p-0  box-border",
          props.primaryLink?.href &&
            "border border-grey hover:-translate-y-[3px] hover:shadow-[0px_6px_12px_0px_rgba(34,34,34,0.08),0px_1px_3px_0px_rgba(34,34,34,0.1)]",
          "[&_.chip]:absolute [&_.chip]:top-6 [&_.chip]:left-6 [&_.button]:justify-between [&_a]:no-underline md:p-6 md:gap-7 md:flex-row md:[&_.chip]:relative md:[&_.chip]:top-auto md:[&_.chip]:left-auto aaa-animate",
        )}
      >
        {props.primaryLink?.href && <Link className="absolute inset-0" href={props.primaryLink.href} />}
        <div
          className={cn(
            "aaa-animate relative pointer-events-none [&_div]:block [&_div]:w-full [&_div]:absolute [&_div]:inset-0 [&>div]:!h-full [&>div_img]:block w-full pt-[calc(100%*(288/408))] rounded-[6px] overflow-hidden md:w-[calc((100%-var(--spacing-7))*(5/12))] md:pt-[calc(((100%-var(--spacing-7))*(5/12))*(288/408))]",
            props.imagePosition && !isMobile ? "order-2" : "order-1",
          )}
        >
          <ResponsiveImage {...props.image} aspect={408 / 288} />
        </div>
        <div
          className={cn(
            "flex flex-col pointer-events-none [&_h1]:type-title-xl [&_h3]:type-title-m [&_h3]:my-0 [&_strong]:!font-[inherit] w-full items-start justify-between p-6 md:w-[calc((100%-var(--spacing-7))*(7/12))] md:pt-2 md:pr-6 md:pb-2 md:pl-0",
            props.imagePosition && !isMobile ? "order-1" : "order-2",
          )}
        >
          <div>
            {props.chip?.children && (
              <>
                <Chip color="grey">{props.chip.children}</Chip>
                {!isMobile && <VSpace initial={5} />}
              </>
            )}
            <Heading as="h3">{props.title}</Heading>
            <VSpace initial={5} />
            <Typography as="p" dangerouslySetInnerHTML={{ __html: props.content ?? "" }} />
          </div>

          {(props.primaryLink?.children || props.secondaryLink?.children) && (
            <div className="flex flex-wrap gap-4 relative z-[5] pointer-events-auto mt-7 w-full flex-col md:flex-row">
              {props.primaryLink?.children && (
                <Button
                  color="yellow"
                  size="medium"
                  icon={props.primaryLink.icon}
                  iconStyle={props.primaryLink.iconStyle}
                  href={props.primaryLink.href}
                >
                  {props.primaryLink.children}
                </Button>
              )}

              {props.secondaryLink?.children && (
                <Button
                  color="grey"
                  size="medium"
                  icon={props.secondaryLink.icon}
                  iconStyle={props.secondaryLink.iconStyle}
                  href={props.secondaryLink.href}
                >
                  {props.secondaryLink.children}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    )
  }

  if (props.type === "multi") {
    return (
      <div className="w-full flex rounded-md gap-4 relative overflow-hidden bg-bg transition-[background-color] ease-movement duration-300 [&_img]:transition-transform [&_img]:ease-movement [&_img]:duration-300 [&_video]:transition-transform [&_video]:ease-movement [&_video]:duration-300 p-5 border border-grey flex-col [&_a]:no-underline aaa-animate hover:-translate-y-[3px] hover:shadow-[0px_6px_12px_0px_rgba(34,34,34,0.08),0px_1px_3px_0px_rgba(34,34,34,0.1)]">
        <div className="aaa-animate relative pointer-events-none [&_div]:block [&_div]:w-full [&_div]:absolute [&_div]:inset-0 [&>div]:!h-full [&>div_img]:block w-full pt-[calc(100%*(189/336))] rounded-[6px] overflow-hidden bg-light-grey [&_svg]:absolute [&_svg]:top-1/2 [&_svg]:left-1/2 [&_svg]:-translate-x-1/2 [&_svg]:-translate-y-1/2 [&_svg]:text-white [&_svg]:w-[40%] [&_svg]:h-auto [&_svg_path]:fill-white">
          {props?.image ? <ResponsiveImage {...props.image} aspect={336 / 189} /> : <Logo />}
        </div>
        <div className="flex flex-col justify-center pointer-events-none [&_h1]:type-title-xl [&_h3]:type-title-m [&_h3]:my-0 [&_strong]:!font-[inherit] [&_h4]:m-0 [&_h4]:text-left">
          <div>
            {props.chip?.children && (
              <>
                <Chip color="grey">{props.chip.children}</Chip>
                {!isMobile && <VSpace initial={5} />}
              </>
            )}
            <Heading as="h4">{props.title}</Heading>
            <VSpace initial={3} />
            <Typography as="p">{props.content}</Typography>
          </div>
        </div>
      </div>
    )
  }
}

export const CardMoreInfo = ({
  ancestor,
  title,
  heading,
  content,
  image,
  link,
  type,
  buttonTextOverride,
}: {
  ancestor?: {
    uri: string
    title: string
  }
  title: string
  heading?: string
  content?: string
  image?: string
  link: string
  type: "child" | "sibling"
  buttonTextOverride?: string
}) => {
  return (
    <div
      className={cn(
        "flex flex-col p-6 gap-6 rounded-xl justify-between md:py-6 md:px-7 md:gap-8 md:flex-row",
        type === "child" ? "bg-white ring-1 ring-inset ring-grey my-4" : "bg-light-grey my-4",
      )}
    >
      <div
        className={cn(
          "flex flex-col justify-between w-full items-start order-2",
          "[&_button.button]:w-full [&_a.button]:w-full [&_button.button_span]:grow [&_button.button_span]:text-left [&_a.button_span]:grow [&_a.button_span]:text-left",
          "md:w-[calc((100%-var(--spacing-8))*(3/5))] md:order-1 md:[&_button.button]:w-auto md:[&_a.button]:w-auto md:[&_button.button_span]:grow-0 md:[&_button.button_span]:text-center md:[&_a.button_span]:grow-0 md:[&_a.button_span]:text-center",
        )}
      >
        <div className="flex flex-col gap-4">
          <div className="flex gap-1">
            {ancestor && (
              <Chip color={type === "child" ? "grey" : "white"} href={ancestor.uri}>
                {ancestor.title}
              </Chip>
            )}
            <Chip color={type === "child" ? "grey" : "white"} href={link}>
              {title}
            </Chip>
          </div>

          <Heading as="h2" variant="h1">
            {heading}
          </Heading>
          <Typography as="p" dangerouslySetInnerHTML={{ __html: content ?? "" }} />
        </div>

        <VSpace initial={8} />
        <Button size="medium" color="yellow" href={link} icon="ARROW_RIGHT" iconStyle="right">
          {buttonTextOverride ? buttonTextOverride : `Learn more`}
        </Button>
      </div>
      {image && (
        <div className="relative rounded-[6px] overflow-hidden w-full order-1 md:w-[calc((100%-var(--spacing-8))*(2/5))] md:order-2">
          <ResponsiveImage mediaItemUrl={image.replace("http://", "//")} aspect={496 / 408} />
        </div>
      )}
    </div>
  )
}

type TimedCardT = {
  chips?: ChipT[]
  title?: string
  heading?: string
  content?: string
  image?: ResponsiveImageFragment
  imageLink?: string
  imagePosition?: boolean
  primaryLink?: ButtonT
  secondaryLink?: ButtonT
}

export const TimedCards = (props: TimedCardT) => {
  return (
    <div
      className={cn(
        "w-full flex rounded-md relative overflow-hidden bg-bg transition-[background-color] ease-movement duration-300 [&_img]:transition-transform [&_img]:ease-movement [&_img]:duration-300 [&_video]:transition-transform [&_video]:ease-movement [&_video]:duration-300 aaa-animate flex-col h-auto gap-0 md:flex-row-reverse md:justify-between md:box-border md:wp:w-full md:wp:!ml-0 hover:-translate-y-[3px] hover:shadow-[0px_6px_12px_0px_rgba(34,34,34,0.08),0px_1px_3px_0px_rgba(34,34,34,0.1)]",
        "p-6 md:p-6 w-full md:w-full ml-0 md:ml-0",
      )}
    >
      {props.primaryLink?.href && <Link className="absolute inset-0" href={props.primaryLink.href} />}

      <div
        className={cn(
          "aaa-animate relative pointer-events-none [&_div]:block [&_div]:w-full [&_div]:absolute [&_div]:inset-0 [&>div]:!h-full [&>div_img]:block w-full h-0 overflow-hidden pt-[calc(100%*(4/6))] bg-light-grey [&_svg]:absolute [&_svg]:top-1/2 [&_svg]:left-1/2 [&_svg]:-translate-x-1/2 [&_svg]:-translate-y-1/2 [&_svg]:text-white [&_svg]:w-[40%] [&_svg]:h-auto [&_svg_path]:fill-white md:w-[calc((100%-var(--tw-grid-gutter))*(6/12))]",
          "md:pt-[calc(((100%-var(--tw-grid-gutter))*(6/12))*(408/496))] md:h-0",
        )}
      >
        {props?.imageLink ? (
          <ResponsiveImage mediaItemUrl={props.imageLink.replace("http://", "//")} aspect={496 / 408} />
        ) : (
          <Logo />
        )}
      </div>

      <div
        className={cn(
          "flex flex-col pointer-events-none [&_h1]:type-title-xl [&_h3]:type-title-m [&_h3]:my-0 [&_strong]:!font-[inherit] p-6 items-start gap-5 md:w-[calc((100%-var(--tw-grid-gutter))*(5.5/12))] md:justify-start md:p-0 xl:w-[calc((100%-var(--tw-grid-gutter))*(5/12))] xl:min-h-[450px]",
          "justify-between",
        )}
      >
        <div>
          {props.chips && (
            <div className="flex gap-x-1 gap-y-1 flex-wrap mt-3 z-[1] pointer-events-auto absolute top-0 left-0 px-3 md:relative md:px-0 md:order-[-1]">
              {props.chips.map((chip, i) => (
                <Chip key={i} color={chip.color} onClick={chip.onClick}>
                  {chip.children}
                </Chip>
              ))}
            </div>
          )}
          <VSpace initial={5} />
          <Heading as="h2" variant="h1">
            {props.heading}
          </Heading>
          <VSpace initial={5} />
          <Typography variant="text-m" as="div" dangerouslySetInnerHTML={{ __html: props?.content ?? "" }} />
        </div>

        {props.primaryLink?.children && (
          <div className="flex flex-wrap gap-4 relative z-[5] pointer-events-auto mt-7 w-full flex-col md:flex-row">
            <Button color="yellow" size="medium" icon={props.primaryLink.icon} iconStyle={props.primaryLink.iconStyle}>
              {props.primaryLink.children}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
